import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { getFadeColor } from '@repo/toolkit/colors'
import Badge from '@repo/ui/badge'
import CapsuleInformation from '@repo/ui/capsule-information'
import CapsuleTab, { Tab } from '@repo/ui/capsule-tab'
import { BarGraph, PieChart } from '@repo/ui/graphs'
import { HouseOneIcon, HouseTwoIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import { getCenterAgeDistribution } from '../../../../api/centers/center-global-statistics/get-center-age-distribution'
import { getCenterFamilyArrangementDistribution } from '../../../../api/centers/center-global-statistics/get-center-family-arrangement-percentage'
import { getCenterFamilyAssignmentDistribution } from '../../../../api/centers/center-global-statistics/get-center-family-assignment'
import { getCenterGenderDistribution } from '../../../../api/centers/center-global-statistics/get-center-gender-percentage'
import { getCenterJourneyDistribution } from '../../../../api/centers/center-global-statistics/get-center-journey-distribution'
import { getCenterMainTripDistribution } from '../../../../api/centers/center-global-statistics/get-center-main-trip-distribution'
import { getCenterSchoolLevelDistribution } from '../../../../api/centers/center-global-statistics/get-center-school-level-distribution'
import { getCenterSecondaryTripDistribution } from '../../../../api/centers/center-global-statistics/get-center-secondary-trip-distribution'
import { hasEnrollmentCenter } from '../../../../api/centers/center-global-statistics/has-enrollment-center'
import {
  SchoolLevelEnum,
  schoolLevelEnumMapping
} from '../../../../lib/types/enums/common/school-level.enum'
import { Center } from '../../../../lib/types/models/center/center'
import {
  AgeRange,
  ageRangeMapping,
  CenterFamilyAssignmentsDistribution,
  CenterJourneyDistribution,
  CenterTripDistribution,
  EnrollmentCenterAgeDistribution,
  EnrollmentCenterFamilyArrangementDistribution,
  EnrollmentCenterGenderDistribution,
  EnrollmentCenterSchoolLevelDistribution
} from '../../../../lib/types/models/center/center-distributions'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import LabeledCard from '../../../elements/labeled-card'
import Legend from '../../../elements/legend'

type Props = {
  center: Center
}

// Memoized chart components to prevent unnecessary re-renders
const GenderDistributionChart = ({
  genderData
}: {
  genderData: EnrollmentCenterGenderDistribution
}) => (
  <LabeledCard title="Répartition par sexe">
    <PieChart
      legend={false}
      labelListClassName="fill-black font-bold"
      data={[
        {
          fill: tailwindColors.red.light.active,
          value: genderData?.FEMALE.count ?? 0,
          key: 'F'
        },
        {
          fill: tailwindColors.blue.light.active,
          value: genderData?.MALE.count ?? 0,
          key: 'M'
        }
      ]}
    />
    <div className="flex flex-col items-center mb-4">
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        Sexes
      </Typography>
      <div className="h-2" />
      <div className="flex items-center gap-1">
        <Badge
          text={`M : ${genderData?.MALE.percentage ?? 0}% (${genderData?.MALE.count ?? 0})`}
          variant="blue"
        />
        <Badge
          text={`F : ${genderData?.FEMALE.percentage ?? 0}% (${genderData?.FEMALE.count ?? 0})`}
          variant="danger"
        />
      </div>
    </div>
  </LabeledCard>
)

const AgeDistributionChart = ({
  ageDistributionData
}: {
  ageDistributionData: EnrollmentCenterAgeDistribution
}) => (
  <LabeledCard title="Répartition par tranche d'âge">
    <BarGraph
      hasborder={false}
      barSize={75}
      showXAxisLine={false}
      barRadius={18}
      showYAxisValue={false}
      title=""
      data={
        Object.entries(ageDistributionData).map(([key, value]) => ({
          fill: tailwindColors.purple.light.active,
          hoverFill: tailwindColors.purple.DEFAULT,
          value,
          key: ageRangeMapping[key as AgeRange]
        })) || []
      }
    />
  </LabeledCard>
)

const FamilyArrangementChart = ({
  familyArrangementData
}: {
  familyArrangementData: EnrollmentCenterFamilyArrangementDistribution
}) => (
  <LabeledCard title="Répartition par modalité d'accueil">
    <PieChart
      legend={false}
      labelListClassName="fill-black font-bold"
      data={[
        {
          fill: tailwindColors.navyBlue.light.active,
          value: familyArrangementData?.FA1E.count ?? 0,
          key: 'FA1E'
        },
        {
          fill: tailwindColors.secondary.light.hover,
          value: familyArrangementData?.FA2E.count ?? 0,
          key: 'FA2E'
        }
      ]}
    />
    <div className="flex flex-col items-center gap-1">
      <Badge
        iconPosition="start"
        icon={<HouseOneIcon color={tailwindColors.navyBlue.DEFAULT} />}
        text={`FA1E : ${familyArrangementData?.FA1E.percentage ?? 0}%`}
        textColor={tailwindColors.navyBlue.DEFAULT}
        color={tailwindColors.navyBlue.light.DEFAULT}
      />
      <Badge
        iconPosition="start"
        icon={<HouseTwoIcon color={tailwindColors.secondary.DEFAULT} />}
        text={`FA2E : ${familyArrangementData?.FA2E.percentage ?? 0}%`}
        variant="warning"
      />
    </div>
  </LabeledCard>
)

const SchoolLevelChart = ({
  schoolLevelData
}: {
  schoolLevelData: EnrollmentCenterSchoolLevelDistribution
}) => (
  <div className="col-span-2">
    <LabeledCard title="Répartition par classe">
      <BarGraph
        hasborder={false}
        barSize={75}
        showXAxisLine={false}
        barRadius={15}
        showYAxisValue={false}
        title=""
        data={
          schoolLevelData
            ? Object.entries(schoolLevelData).map(([key, value]) => ({
                fill: tailwindColors.blue.light.active,
                hoverFill: tailwindColors.blue.DEFAULT,
                value,
                key: schoolLevelEnumMapping[key as SchoolLevelEnum]
              }))
            : []
        }
      />
    </LabeledCard>
  </div>
)

const FamilyAssignmentsDistributionContent = ({
  familyAssignmentsData
}: {
  familyAssignmentsData: Extract<
    CenterFamilyAssignmentsDistribution,
    { isHostFamilyAccomodation: true }
  >
}) => (
  <LabeledCard title="Assignation des familles">
    <div className="py-12 px-5 flex flex-col">
      <Typography variant="label-semibold" className="mb-2">
        Jeunes ({familyAssignmentsData?.enrollments.total ?? 0})
      </Typography>
      <CapsuleInformation
        hideTotal
        items={[
          {
            background: tailwindColors.purple.DEFAULT,
            color: tailwindColors.white.DEFAULT,
            value: familyAssignmentsData?.enrollments.withHostFamily ?? 0
          },
          {
            background: tailwindColors.purple.light.active,
            color: tailwindColors.purple.DEFAULT,
            value: Math.max(
              0,
              (familyAssignmentsData?.enrollments.total ?? 0) -
                (familyAssignmentsData?.enrollments.withHostFamily ?? 0)
            )
          }
        ]}
      />
      <div className="h-1" />
      <div className="flex items-center justify-between">
        <Legend
          color={tailwindColors.purple.DEFAULT}
          text={`Avec famille (${familyAssignmentsData?.enrollments.percentageWithHostFamily ?? 0}%)`}
        />
        <Legend
          color={tailwindColors.purple.light.active}
          text={`Sans famille (${
            100 - (familyAssignmentsData?.enrollments.percentageWithHostFamily ?? 0)
          }%)`}
        />
      </div>
    </div>

    <div className="px-5 flex flex-col">
      <Typography variant="label-semibold" className="mb-2">
        Encadrants ({familyAssignmentsData?.affectedStaff.total ?? 0})
      </Typography>
      {familyAssignmentsData?.affectedStaff.total === 0 ? (
        <Typography variant="tag" color={tailwindColors.grey.light.active}>
          Aucune donnée disponible
        </Typography>
      ) : (
        <>
          <CapsuleInformation
            hideTotal
            items={[
              {
                background: tailwindColors.blue.DEFAULT,
                color: tailwindColors.white.DEFAULT,
                value: familyAssignmentsData?.affectedStaff.withHostFamily ?? 0
              },
              {
                background: tailwindColors.blue.light.active,
                color: tailwindColors.blue.DEFAULT,
                value: Math.max(
                  0,
                  (familyAssignmentsData?.affectedStaff.total ?? 0) -
                    (familyAssignmentsData?.affectedStaff.withHostFamily ?? 0)
                )
              }
            ]}
          />
          <div className="h-1" />
          <div className="flex items-center justify-between">
            <Legend
              color={tailwindColors.blue.DEFAULT}
              text={`Avec famille (${familyAssignmentsData?.affectedStaff.percentageWithHostFamily ?? 0}%)`}
            />
            <Legend
              color={tailwindColors.blue.light.active}
              text={`Sans famille (${100 - (familyAssignmentsData?.affectedStaff.percentageWithHostFamily ?? 0)}%)`}
            />
          </div>
        </>
      )}
    </div>
  </LabeledCard>
)

const FamilyAssignmentsSection = ({
  familyAssignmentsData
}: {
  familyAssignmentsData: CenterFamilyAssignmentsDistribution
}) =>
  familyAssignmentsData.isHostFamilyAccomodation ? (
    <FamilyAssignmentsDistributionContent familyAssignmentsData={familyAssignmentsData} />
  ) : (
    <LabeledCard title="Assignation des familles">
      <div className="w-full text-center justify-center flex items-center h-full">
        <Typography variant="secondary-body">Aucune donnée à afficher</Typography>
      </div>
    </LabeledCard>
  )

const TripDistributionCharts = ({
  mainTripsData,
  secondaryTripsData
}: {
  mainTripsData: CenterTripDistribution[]
  secondaryTripsData: CenterTripDistribution[]
}) => (
  <div className="grid grid-cols-2 gap-3.5 mt-4">
    <LabeledCard title="Répartition par voyage principal">
      <BarGraph
        hasborder={false}
        barSize={100}
        showXAxisLine={false}
        barRadius={15}
        showYAxisValue={false}
        title=""
        data={mainTripsData.map((data) => ({
          fill: tailwindColors.blue.light.active,
          hoverFill: tailwindColors.blue.DEFAULT,
          value: data.count,
          key: TripProperties.getTripLabel(data.trip)
        }))}
      />
    </LabeledCard>

    <LabeledCard title="Répartition par voyage secondaire">
      {secondaryTripsData && Object.keys(secondaryTripsData).length > 0 ? (
        <BarGraph
          hasborder={false}
          barSize={100}
          showXAxisLine={false}
          barRadius={15}
          showYAxisValue={false}
          title=""
          data={secondaryTripsData.map((data) => ({
            fill: tailwindColors.blue.light.active,
            hoverFill: tailwindColors.blue.DEFAULT,
            value: data.count,
            key: TripProperties.getTripLabel(data.trip)
          }))}
        />
      ) : (
        <Typography
          variant="label-semibold"
          className="p-5"
          color={tailwindColors.grey.light.active}
        >
          Aucune donnée disponible
        </Typography>
      )}
    </LabeledCard>
  </div>
)

const JourneyDistributionChart = ({
  journeysData
}: {
  journeysData: CenterJourneyDistribution[]
}) => (
  <LabeledCard title="Répartition par séjour">
    <div className="h-10" />
    <BarGraph
      hasborder={false}
      barSize={100}
      barRadius={15}
      tickLine
      yAxisLabel="Nombre"
      xAxisLabel="Séjour"
      title=""
      data={journeysData.map((data) => ({
        fill: getFadeColor(data.journey.date.period?.color ?? tailwindColors.blue.DEFAULT, 80),
        hoverFill: data.journey.date.period?.color ?? tailwindColors.blue.DEFAULT,
        value: data.value,
        key: data.journey.reference
      }))}
    />
    <div className="h-4" />
  </LabeledCard>
)

const CenterGlobalViewStatisticsSection = ({ center }: Props) => {
  const [activeTab, setActiveTab] = useState(0)

  // Memoized tabs configuration
  const tabs = useMemo((): Tab<'required-key'>[] => {
    const journeyTabs = center.centerToJourneys.map((centerToJourney) => ({
      key: centerToJourney.journey.id.toString(),
      label: centerToJourney.journey.reference
    }))

    return [{ key: 'all-journeys', label: 'Tous les séjours' }, ...journeyTabs]
  }, [center.centerToJourneys])

  // Get selected journey ID
  const selectedJourneyId = useMemo(() => {
    const key = tabs[activeTab]?.key
    return key !== 'all-journeys' ? Number(key) : undefined
  }, [activeTab, tabs])

  // Memoized query functions to prevent unnecessary re-renders
  const queryFunctions = useMemo(
    () => ({
      hasEnrollment: () => hasEnrollmentCenter(center.id, selectedJourneyId),
      gender: () => getCenterGenderDistribution(center.id, selectedJourneyId),
      familyArrangement: () =>
        getCenterFamilyArrangementDistribution(center.id, selectedJourneyId),
      ageDistribution: () => getCenterAgeDistribution(center.id, selectedJourneyId),
      schoolLevel: () => getCenterSchoolLevelDistribution(center.id, selectedJourneyId),
      secondaryTrips: () => getCenterSecondaryTripDistribution(center.id, selectedJourneyId),
      mainTrips: () => getCenterMainTripDistribution(center.id, selectedJourneyId),
      journeys: () => getCenterJourneyDistribution(center.id),
      familyAssignments: () => getCenterFamilyAssignmentDistribution(center.id, selectedJourneyId)
    }),
    [center.id, selectedJourneyId]
  )

  // Data queries with stable query functions
  const { data: hasEnrollmentData } = useQuery({
    queryKey: ['has-enrollment-center', center.id, selectedJourneyId],
    queryFn: queryFunctions.hasEnrollment
  })

  const { data: genderData } = useQuery({
    queryKey: ['gender-distribution', center.id, selectedJourneyId],
    queryFn: queryFunctions.gender
  })

  const { data: familyArrangementData } = useQuery({
    queryKey: ['family-arrangement-distribution', center.id, selectedJourneyId],
    queryFn: queryFunctions.familyArrangement
  })

  const { data: ageDistributionData } = useQuery({
    queryKey: ['age-distribution', center.id, selectedJourneyId],
    queryFn: queryFunctions.ageDistribution
  })

  const { data: schoolLevelData } = useQuery({
    queryKey: ['school-level', center.id, selectedJourneyId],
    queryFn: queryFunctions.schoolLevel
  })

  const { data: secondaryTripsData } = useQuery({
    queryKey: ['secondary-trips', center.id, selectedJourneyId],
    queryFn: queryFunctions.secondaryTrips
  })

  const { data: mainTripsData } = useQuery({
    queryKey: ['main-trips', center.id, selectedJourneyId],
    queryFn: queryFunctions.mainTrips
  })

  const { data: journeysData } = useQuery({
    queryKey: ['journeys-distribution', center.id],
    queryFn: queryFunctions.journeys
  })

  const { data: familyAssignmentsData } = useQuery({
    queryKey: ['family-assignments', center.id, selectedJourneyId],
    queryFn: queryFunctions.familyAssignments
  })

  // Memoized tab change handler
  const handleTabChange = useCallback((tabIndex: number) => {
    setActiveTab(tabIndex)
  }, [])

  //add loader if all data is not loaded
  if (
    !hasEnrollmentData &&
    !genderData &&
    !familyArrangementData &&
    !ageDistributionData &&
    !schoolLevelData &&
    !secondaryTripsData &&
    !mainTripsData &&
    !journeysData &&
    !familyAssignmentsData
  ) {
    return (
      <InnerBox className="rounded-xl">
        <div className="flex items-center justify-center h-64">
          <Loader center />
        </div>
      </InnerBox>
    )
  }

  // Main render
  return (
    <InnerBox className="rounded-xl">
      <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
        Statistiques
      </Typography>

      <div className="h-3.5" />

      <CapsuleTab
        className="w-fit p-2"
        size="small"
        variant="secondary"
        onTabChange={handleTabChange}
        tabs={tabs}
        activeTab={activeTab}
      />

      <div className="h-5" />

      {!hasEnrollmentData ? (
        <div className="text-center text-red-500">
          <Typography variant="label-semibold">
            Aucune donnée disponible pour ce séjour.
          </Typography>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3.5">
            {genderData && <GenderDistributionChart genderData={genderData} />}
            {ageDistributionData && (
              <AgeDistributionChart ageDistributionData={ageDistributionData} />
            )}
            {familyArrangementData && (
              <FamilyArrangementChart familyArrangementData={familyArrangementData} />
            )}
            {schoolLevelData && <SchoolLevelChart schoolLevelData={schoolLevelData} />}
            {familyAssignmentsData && (
              <FamilyAssignmentsSection familyAssignmentsData={familyAssignmentsData} />
            )}
          </div>

          {mainTripsData && secondaryTripsData && (
            <TripDistributionCharts
              mainTripsData={mainTripsData}
              secondaryTripsData={secondaryTripsData}
            />
          )}

          <div className="h-4" />

          {activeTab === 0 && journeysData && (
            <JourneyDistributionChart journeysData={journeysData} />
          )}
        </>
      )}
    </InnerBox>
  )
}

export default CenterGlobalViewStatisticsSection
